import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { User, AuthTokens, LoginCredentials } from '../types';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: { user: User; tokens: AuthTokens } }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'LOGOUT' };

const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_USER':
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    
    default:
      return state;
  }
};

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const storedTokens = authService.getStoredTokens();
      const storedUser = authService.getStoredUser();

      if (storedTokens && storedUser) {
        // Check if tokens are still valid
        if (authService.isTokenValid(storedTokens.accessToken)) {
          dispatch({
            type: 'SET_USER',
            payload: { user: storedUser, tokens: storedTokens },
          });
          return;
        }

        // Try to refresh token
        if (storedTokens.refreshToken) {
          await refreshToken();
          return;
        }
      }

      // No valid authentication found
      authService.clearStoredAuth();
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      console.error('Auth initialization error:', error);
      authService.clearStoredAuth();
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await authService.login(credentials);
      
      if (response.success && response.data) {
        const { user, tokens } = response.data;
        
        // Store authentication data
        authService.storeTokens(tokens);
        authService.storeUser(user);
        
        dispatch({
          type: 'SET_USER',
          payload: { user, tokens },
        });

        toast.success(`Welcome back, ${user.firstName}!`);
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Login failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
    }
  };

  const logout = () => {
    authService.clearStoredAuth();
    dispatch({ type: 'LOGOUT' });
    toast.success('Logged out successfully');
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const refreshToken = async () => {
    try {
      const storedTokens = authService.getStoredTokens();
      
      if (!storedTokens?.refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authService.refreshToken(storedTokens.refreshToken);
      
      if (response.success && response.data) {
        const newTokens = response.data.tokens;
        const storedUser = authService.getStoredUser();
        
        if (!storedUser) {
          throw new Error('No user data available');
        }

        // Update stored tokens
        authService.storeTokens({
          ...storedTokens,
          ...newTokens,
        });

        dispatch({
          type: 'SET_USER',
          payload: {
            user: storedUser,
            tokens: { ...storedTokens, ...newTokens },
          },
        });
      } else {
        throw new Error(response.error || 'Token refresh failed');
      }
    } catch (error: any) {
      console.error('Token refresh error:', error);
      authService.clearStoredAuth();
      dispatch({ type: 'LOGOUT' });
      toast.error('Session expired. Please log in again.');
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    clearError,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 